/**
 * Treatment plans (WP2-TX, BUILD-PLAN M08): the plan worklist and
 * unscheduled treatment, the case board with options, phases and visits,
 * estimates that explain themselves, chairside presentation, e-sign and
 * financing. The patient's phone page is served by the API at
 * `/v1/txplan/p/<token>`.
 */
import { defineManifest } from '@velora/contracts';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'txplan',
    title: 'Treatment plans',
    version: '0.1.0',
    owner: 'WP2-TX',
    entitlementKey: null,
    permissions: [
      'txplan.view',
      'txplan.edit',
      'txplan.acceptance_report',
      'money.view',
      'patient.view',
    ],
    routes: [
      {
        id: 'plans',
        path: '/treatment-plans',
        title: 'Treatment plans',
        officeScope: 'location-or-all',
        permission: 'txplan.view',
        readsPhi: true,
      },
      {
        // MTX-TX-25: the palette's "Unscheduled treatment worklist" opens the
        // plans page with the follow-up list selected (redirects to
        // /treatment-plans?f=unscheduled).
        id: 'unscheduled',
        path: '/treatment-plans/unscheduled',
        title: 'Unscheduled treatment',
        officeScope: 'location-or-all',
        permission: 'txplan.view',
        readsPhi: false,
      },
      {
        id: 'patient',
        path: '/patients/$patientId/treatment',
        title: 'Treatment plans',
        officeScope: 'location-or-all',
        permission: 'txplan.view',
        readsPhi: true,
      },
      {
        // TP-23 / TX-16: consent text, financial policy, verbal acceptance,
        // informed refusal, lender offers and the pay-in-full discount.
        id: 'settings',
        path: '/treatment-plans/settings',
        title: 'Treatment plan settings',
        officeScope: 'location',
        permission: 'txplan.edit',
        readsPhi: false,
      },
      {
        // TC role test fix 4: case acceptance by provider, office and month,
        // with its own narrow right (not all of Analytics).
        id: 'acceptance',
        path: '/treatment-plans/acceptance',
        title: 'Case acceptance',
        officeScope: 'location-or-all',
        permission: 'txplan.acceptance_report',
        readsPhi: false,
      },
      {
        // TC role test fix 8: predeterminations sent and waiting.
        id: 'predeterminations',
        path: '/treatment-plans/predeterminations',
        title: 'Predeterminations',
        officeScope: 'location-or-all',
        permission: 'txplan.view',
        readsPhi: true,
      },
    ],
    nav: [
      {
        label: 'Treatment Plans',
        labelKey: 'txPlan',
        group: 'clinical',
        routeId: 'plans',
        icon: 'clipboard-list',
        order: 30,
        permission: 'txplan.view',
      },
      // Tabs of Treatment plans (sidebar MERGED_INTO): All modules, pins, Ctrl+K.
      {
        label: 'Case acceptance',
        group: 'clinical',
        routeId: 'acceptance',
        icon: 'trending-up',
        order: 31,
        permission: 'txplan.acceptance_report',
      },
      {
        label: 'Predeterminations',
        group: 'clinical',
        routeId: 'predeterminations',
        icon: 'file-text',
        order: 32,
        permission: 'txplan.view',
      },
    ],
    commands: [
      {
        id: 'txplan.open',
        title: 'Go to Treatment plans',
        keywords: ['tx plan', 'case presentation', 'estimate', 'acceptance', 'treatment'],
        routeId: 'plans',
        permission: 'txplan.view',
      },
      {
        id: 'txplan.unscheduled',
        title: 'Unscheduled treatment worklist',
        keywords: ['follow up', 'unscheduled', 'treatment finder', 'money finder'],
        routeId: 'unscheduled',
        permission: 'txplan.view',
      },
      {
        id: 'txplan.acceptance',
        title: 'Case acceptance report',
        keywords: ['acceptance rate', 'case acceptance', 'provider', 'month', 'scorecard'],
        routeId: 'acceptance',
        permission: 'txplan.acceptance_report',
      },
      {
        id: 'txplan.predeterminations',
        title: 'Predeterminations waiting',
        keywords: ['pre-authorization', 'preauth', 'pre-treatment estimate', 'predetermination'],
        routeId: 'predeterminations',
        permission: 'txplan.view',
      },
      {
        id: 'txplan.settings',
        title: 'Treatment plan settings',
        keywords: ['consent', 'financial policy', 'verbal acceptance', 'refusal', 'financing'],
        routeId: 'settings',
        permission: 'txplan.edit',
      },
    ],
    shortcuts: [{ keys: 'g t', description: 'Go to Treatment plans', routeId: 'plans' }],
    sseEntityKeys: ['tx_case', 'procedure'],
    phiReadPoints: ['txplan.case', 'txplan.worklist'],
  }),
  pages: {
    plans: () => import('./pages/TxPlansPage.js'),
    unscheduled: () => import('./pages/UnscheduledRedirect.js'),
    patient: () => import('./pages/PatientPlansPage.js'),
    settings: () => import('./pages/Settings.js'),
    acceptance: () => import('./pages/AcceptancePage.js'),
    predeterminations: () => import('./pages/PredeterminationsPage.js'),
  },
});
